
import { defineComponent } from '@vue/composition-api';
import IllinoisIcons from '../icons.vue';
import SummaryErrors from './SummaryErrors.vue';

export default defineComponent({
  name: 'IllinoisSummary',
  components: {
    IllinoisIcons,
    SummaryErrors
  },
  data() {
    return {
      contentHeight: 'auto' as string
    };
  },
  computed: {
    getBet(): any {
      return this.$store.getters['cart/getBet'];
    },
    getSettings(): any {
      return this.$store.getters['cart/getSettings'];
    },
    getBetSystemParts(): any[] {
      return this.$store.getters['cart/getBetSystemParts'];
    },
    systemCombosValid(): any[] {
      const parts = this.getBetSystemParts || [];
      return parts.filter((c: any) => c && c.valid_combinations > 0);
    },
    displayedErrorsMultiInfo(): Array<{ message: string }> {
      if (
        this.getBet.errors_multi_info &&
        this.getBet.errors_multi_info.length > 0
      ) {
        return this.getBet.errors_multi_info;
      }

      // Per il sistema, la molteplicità non si applica globalmente
      if (this.getBet.type === 'system') {
        return [];
      }

      // Fallback: replica di multiplicityVincolsInfo
      let multiplicity = 0;
      if (this.getBet.type === 'single' || this.getBet.type === 'multiple') {
        multiplicity = +this.getBet._multiple_detail.multiplicity;
      } else if (this.getBet.type === 'integral') {
        multiplicity = +this.getBet._integral_detail.multiplicity;
      }
      let multiMinimum = 0;
      for (const odd of this.getBet.odds || []) {
        if (odd && +odd.multiplicity_min > multiMinimum)
          multiMinimum = +odd.multiplicity_min;
      }
      if (multiplicity < multiMinimum && multiMinimum > 0) {
        return [{ message: `multiplicity_info_${multiMinimum}` }];
      }
      return [];
    },
    displayedErrors(): Array<{ message: string; value?: number }> {
      if (this.getBet.errors && this.getBet.errors.length > 0) {
        return this.getBet.errors;
      }
      // Fallback: replica minima di amountVincols per min/max
      const errors: Array<{ message: string; value?: number }> = [];
      const ctx = this.getBet.context;
      const settings = this.getSettings || {};
      let amount = 0;
      if (this.getBet.type === 'single' || this.getBet.type === 'multiple') {
        amount = +this.getBet._multiple_detail.amount;
      } else if (this.getBet.type === 'integral') {
        amount = +this.getBet._integral_detail.amount;
      } else if (this.getBet.type === 'system') {
        amount = +this.getBet._system_detail.amount_system;
      }
      if (ctx === 'prematch') {
        const minAmount = settings.minAmount;
        const prematchMax = settings.prematch_max_amount;
        if (minAmount != null && +minAmount > 0 && amount < +minAmount) {
          errors.push({
            message: 'minimumAmountNotRespected',
            value: +minAmount
          });
        }
        if (prematchMax != null && +prematchMax > 0 && amount > +prematchMax) {
          errors.push({
            message: 'maximumAmountNotRespected',
            value: +prematchMax
          });
        }
      } else if (ctx === 'live' || ctx === 'mixed') {
        const liveMin = settings.live_min_amount;
        const liveMax = settings.live_max_amount;
        if (liveMin != null && +liveMin > 0 && amount < +liveMin) {
          errors.push({
            message: 'minimumAmountNotRespected',
            value: +liveMin
          });
        }
        if (liveMax != null && +liveMax > 0 && amount > +liveMax) {
          errors.push({
            message: 'maximumAmountNotRespected',
            value: +liveMax
          });
        }
      }
      return errors;
    },
    displayedWarnings(): Array<{ message: string; value?: number }> {
      // Preferisci store; nessun fallback per performance/complessità
      return this.getBet.warnings || [];
    },
    showOddsUpdateMessage(): boolean {
      return this.$store.getters['sport/showOddsUpdateMessage'];
    },
    getPercentageTaxValueOnWon(): number {
      return this.$store.getters['settings/getPercentageTaxValueOnWon'];
    },
    amountTotalComputed() {
      const amount = +this.getBet._integral_detail.amount || 0;
      const combinations = +this.getBet._integral_detail.combinations || 1;

      return this.getSettings.integralDivided ? amount : amount * combinations;
    },
    amountSystemEqual: {
      get(): number | null {
        return this.$store.state.cart.bet._system_detail.amount_system_equal;
      },
      async set(value: number | null) {
        const minAmount = this.$store.state.cart.settings.minAmount;
        const amount = value != null && value >= 0 ? value : minAmount;

        // Verifica se tutte le combinazioni sono unchecked
        const systemParts = this.getBet.system_parts || [];
        const allUnchecked = systemParts.every((part: any) => !part.checked);

        await this.$store.commit('cart/SET_SYSTEM_AMOUNT_TO_DIVIDE', null);
        await this.$store.commit('cart/SET_SYSTEM_AMOUNT_EQUAL', amount);

        // Se tutte sono unchecked, settale tutte come checked
        if (allUnchecked) {
          await this.checkAllComboSystem();
        } else {
          await this.$store.dispatch('cart/refreshCart');
        }
      }
    },
    amountSystemToDivide: {
      get(): number | null {
        return this.$store.state.cart.bet._system_detail
          .amount_system_to_divide;
      },
      async set(value: number | null) {
        const minAmount = this.$store.state.cart.settings.minAmount;
        const amount = value != null && value >= 0 ? value : minAmount;

        // Verifica se tutte le combinazioni sono unchecked
        const systemParts = this.getBet.system_parts || [];
        const allUnchecked = systemParts.every((part: any) => !part.checked);

        await this.$store.commit('cart/SET_SYSTEM_AMOUNT_EQUAL', null);
        await this.$store.commit('cart/SET_SYSTEM_AMOUNT_TO_DIVIDE', amount);

        // Se tutte sono unchecked, settale tutte come checked
        if (allUnchecked) {
          await this.checkAllComboSystem();
        } else {
          await this.$store.dispatch('cart/refreshCart');
        }
      }
    }
  },
  watch: {},
  methods: {
    t(key: string): string {
      return this.$t(key) as string;
    },
    onBeforeLeave(el: HTMLElement) {
      // Salva l'altezza attuale del wrapper prima di uscire
      const wrapper = this.$refs.summaryWrapper as HTMLElement;
      if (wrapper) {
        this.contentHeight = wrapper.scrollHeight + 'px';
      }
    },
    onLeave(el: HTMLElement, done: () => void) {
      // 1. Fade out (200ms)
      el.style.transition = 'opacity 0.2s ease-out';
      el.style.opacity = '0';

      setTimeout(() => {
        done();
      }, 200);
    },
    onBeforeEnter(el: HTMLElement) {
      // Imposta immediatamente invisibile PRIMA che l'elemento venga inserito
      el.style.opacity = '0';
    },
    onEnter(el: HTMLElement, done: () => void) {
      // Già invisibile grazie a onBeforeEnter

      this.$nextTick(() => {
        // 2. Ridimensiona (250ms)
        const newHeight = el.scrollHeight;
        const wrapper = this.$refs.summaryWrapper as HTMLElement;
        if (wrapper) {
          void wrapper.offsetHeight; // Force reflow
        }
        this.contentHeight = newHeight + 'px';

        // Aspetta ridimensionamento (250ms) poi fade in (200ms)
        setTimeout(() => {
          // 3. Fade in (200ms)
          el.style.transition = 'opacity 0.2s ease-out';
          el.style.opacity = '1';

          setTimeout(() => {
            done();
          }, 200);
        }, 250);
      });
    },
    onAfterEnter(el: HTMLElement) {
      this.contentHeight = 'auto';
      el.style.transition = '';
      el.style.opacity = '';
    },
    async refreshCart() {
      await this.$store.dispatch('cart/refreshCart');
    },
    formatNumber(amount: number): string {
      return new Intl.NumberFormat().format(amount);
    },
    roundDigits(value: number, digits: number): number {
      const factor = Math.pow(10, digits);
      return Math.round((value + Number.EPSILON) * factor) / factor;
    },
    oddsTypeFilter(value: number | string): string | number {
      try {
        const filters: any =
          (this as any).$options && (this as any).$options
            ? (this as any).$options.filters
            : undefined;
        if (filters && typeof filters.odds_type_filter === 'function') {
          return filters.odds_type_filter(value);
        }
      } catch (e) {}
      return value;
    },
    async onMultipleAmountInput() {
      if (this.getBet._multiple_detail.amount < 0) {
        this.getBet._multiple_detail.amount = 0;
      }
      await this.$store.dispatch('cart/refreshCart');
    },
    async incMultiple() {
      const step = 0.5;
      this.getBet._multiple_detail.amount = Math.max(
        0,
        +(this.getBet._multiple_detail.amount || 0) + step
      );
      await this.$store.dispatch('cart/refreshCart');
    },
    async decMultiple() {
      const step = 0.5;
      this.getBet._multiple_detail.amount = Math.max(
        0,
        +(this.getBet._multiple_detail.amount || 0) - step
      );
      await this.$store.dispatch('cart/refreshCart');
    },
    async addAmountMultiple(amount: number) {
      if (amount != null && +amount > 0) {
        this.getBet._multiple_detail.amount += +amount;
        await this.$store.dispatch('cart/refreshCart');
      }
    },
    async addAmountIntegral(amount: number) {
      if (amount != null && +amount > 0) {
        this.getBet._integral_detail.amount += +amount;
        await this.$store.dispatch('cart/refreshCart');
      }
    },
    async incIntegral() {
      const step = 0.5;
      this.getBet._integral_detail.amount = Math.max(
        0,
        +(this.getBet._integral_detail.amount || 0) + step
      );
      await this.$store.dispatch('cart/refreshCart');
    },
    async decIntegral() {
      const step = 0.5;
      this.getBet._integral_detail.amount = Math.max(
        0,
        +(this.getBet._integral_detail.amount || 0) - step
      );
      await this.$store.dispatch('cart/refreshCart');
    },
    async refreshSystemCart(combo?: any) {
      await this.$store.dispatch('cart/setIsCartUpdating', true);
      try {
        if (combo) {
          this.getBet._system_detail.amount_system_equal = null;
          this.getBet._system_detail.amount_system_to_divide = null;
          combo.checked = combo.amount > 0.0;
        }
        await this.$store.dispatch('cart/setSystemComboDetail', {
          system_parts: this.getBet.system_parts
        });
        await this.$store.dispatch('cart/refreshCart');
      } finally {
        await this.$store.dispatch('cart/setIsCartUpdating', false);
      }
    },
    async incAmountSystemEqual() {
      const step = 0.5;
      const next = Math.max(0, +((this as any).amountSystemEqual || 0) + step);
      (this as any).amountSystemEqual = +next.toFixed(2);
    },
    async decAmountSystemEqual() {
      const step = 0.5;
      const next = Math.max(0, +((this as any).amountSystemEqual || 0) - step);
      (this as any).amountSystemEqual = +next.toFixed(2);
    },
    async incAmountSystemToDivide() {
      const step = 0.5;
      const next = Math.max(
        0,
        +((this as any).amountSystemToDivide || 0) + step
      );
      (this as any).amountSystemToDivide = +next.toFixed(2);
    },
    async decAmountSystemToDivide() {
      const step = 0.5;
      const next = Math.max(
        0,
        +((this as any).amountSystemToDivide || 0) - step
      );
      (this as any).amountSystemToDivide = +next.toFixed(2);
    },
    async incComboAmount(combo: any) {
      if (!combo) return;
      const step = 0.5;
      combo.amount = Math.max(0, +(combo.amount || 0) + step);
      await this.refreshSystemCart(combo);
    },
    async decComboAmount(combo: any) {
      if (!combo) return;
      const step = 0.5;
      combo.amount = Math.max(0, +(combo.amount || 0) - step);
      await this.refreshSystemCart(combo);
    },
    toggleComboDetail(combo: any) {
      combo.show = !combo.show;
    },
    async checkAllComboSystem() {
      const systemParts = this.getBet.system_parts || [];
      systemParts.forEach((detail: any) => {
        if (detail != undefined && detail.valid_combinations > 0) {
          detail.checked = true;
        }
      });
      await this.$store.dispatch('cart/setSystemComboDetail', {
        system_parts: this.getBet.system_parts
      });
      await this.$store.dispatch('cart/refreshCart');
    },
    getErrorDesc(message: string): string {
      const split = message.split('#');
      return split[0] + ' ' + this.t(split[1]);
    }
  }
});
